import { AfterViewInit, Component, EventEmitter, Input, OnInit, Output, signal, WritableSignal } from '@angular/core';
import { ConfigInput, ConfigSelected } from '../../interfaces/interfaces';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { ListsService } from '../../services/lists.service';
import { SelectedComponent } from "../selected/selected.component";
import { InputComponent } from "../input/input.component";
import { AutocompleteComponent } from '../autocomplete/autocomplete.component';
import { CommonModule } from '@angular/common';
import { minSalaryValidator, MIN_SALARY } from 'app/config/functions.shared';

@Component({
  selector: 'app-independent',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    SelectedComponent,
    InputComponent,
    AutocompleteComponent,
    CommonModule
  ],
  templateUrl: './independent.component.html',
  styleUrl: './independent.component.css'
})
export class IndependentComponent implements OnInit, AfterViewInit {

  form: FormGroup;
  dataInputs: WritableSignal<ConfigInput[]> = signal([
      { label: '¿Cuál es tu ingreso por la actividad económica principal?', subLabel: '', formName: 'income', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: [Validators.required, Validators.max(30000000), minSalaryValidator(MIN_SALARY)], textError: `El ingreso debe ser igual o superior a ${MIN_SALARY.toLocaleString()}` },
    { label: 'Otros Ingresos', subLabel: '', formName: 'incomeExtraActivity', placeholder: '', type: 'number', defaultValue: '', enabled: true, validation: null, textError: 'Por favor ingresa un valor válido' },
  ])

  dataSelected: WritableSignal<ConfigSelected[]> = signal([
    { label: 'Cuéntanos más de tu actividad', subLabel: '', formName: 'indact', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un elemento de la lista', options: signal([]) },
    { label: '¿Cuál es tu nivel educativo?', subLabel: '', formName: 'academic', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor selecciona un nivel educativo', options: signal([]) },
    { label: '¿Perteneces a algún grupo de protección especial constitucional?', subLabel: '', formName: 'specialProtecionProgram', placeholder: '', type: 'text', defaultValue: '', enabled: true, validation: [Validators.required], textError: 'Por favor seleccione un elemento de la lista', options: signal([]) },
  ])

  @Output() formSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Input() defaultData: any;

  codpros: any[] = [];

  constructor(
    public _listService: ListsService
  ) {
    this.form = new FormGroup({
      indact: new FormControl('', [Validators.required]),
      academic: new FormControl('', [Validators.required]),
      specialProtecionProgram: new FormControl('', [Validators.required]),
      econmicActivity: new FormControl('', [Validators.required]),
      income: new FormControl('', [Validators.required, Validators.max(30000000)]),
      codpro: new FormControl('', [Validators.required]),
      incomeExtraActivity: new FormControl(''),
    });

    this.form.valueChanges.subscribe(() => {
      this.emitFormState();
    });
  }
  ngAfterViewInit(): void {
  }

  ngOnInit(): void {
    this.getAllIndAct();
    this.getAllAcademicLevels();
    this.getAllSpecialProgram();
    this.getAllProfessions();

    this.form.patchValue({
      income: this.defaultData?.income ?? '',
      incomeExtraActivity: this.defaultData?.incomeExtraActivity ?? '',
      indact: this.defaultData?.indact ?? '',
      academic: this.defaultData?.academic ?? '',
      specialProtecionProgram: this.defaultData?.specialProtecionProgram ?? '',
      econmicActivity: this.defaultData?.econmicActivity ?? '',
      codpro: this.defaultData?.codpro ?? '',
    });
    if (this.defaultData) {
      this.dataInputs.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultData[input.formName] ?? input.defaultValue
        }))
      );
      this.dataSelected.update((inputs:any) =>
        inputs.map((input:any) => ({
          ...input,
          defaultValue: this.defaultData[input.formName] ?? input.defaultValue
        }))
      );
    }
  }

  getAllProfessions() {
    this._listService.getCodpro().subscribe({
      next: (response: any) => {
        this.codpros = response;
      },
      error: (error: any) => {
        console.error('Error cargando profesiones:', error);
      }
    });
  }

  getAllIndAct() {
    this._listService.getIndAct().subscribe({
      next: (response: any) => {
        this.dataSelected().at(0)?.options.set(response);
      },
      error: (error: any) => {
        console.error('Error loading independent activities:', error);
      }
    });
  }

  getAllSpecialProgram() {
    this._listService.getAllSpecialProgram().subscribe({
      next: (response: any) => {
        this.dataSelected().at(2)?.options.set(response);
      },
      error: (error: any) => {
        console.error('Error loading special programs:', error);
      }
    });
  }

  getAllAcademicLevels() {
    this._listService.getAllAcademicLevels().subscribe({
      next: (response: any) => {
        this.dataSelected().at(1)?.options.set(response);
      },
      error: (error: any) => {
        console.error('Error loading academic levels:', error);
      }
    });
  }

  setEconmicActivity(event: any) {
    this.form.get('econmicActivity')!.setValue(event.id);
  }

  handleValue(event: { formName: string, value: string | number | null }) {
    const control = this.form.get(event.formName);
    const parsed = this.parseFieldValue(event.formName, event.value);
    control?.setValue(parsed);
    control?.markAsTouched();
    control?.updateValueAndValidity();
    this.emitFormState();
  }

  private emitFormState(): void {
    this.formSubmit.emit(this.form.value);
  }

  private parseFieldValue(
    formName: string,
    value: string | number | null | undefined
  ): string | number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const numericFields = ['income', 'incomeExtraActivity'];
    if (!numericFields.includes(formName)) {
      return value;
    }

    const normalized = typeof value === 'number'
      ? value
      : parseFloat(String(value).replace(/,/g, ''));

    return Number.isNaN(normalized) ? null : normalized;
  }

  onProfessionSelected(selectedOption: any): void {
    this.form.get('codpro')!.setValue(selectedOption);
  }
}
